import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import ProductCard from '../components/ProductCard';
import Products2Hero from '../components/Products2Hero';
import { Helmet } from 'react-helmet-async';
import { generateProductSlug } from '../utils/seoUtils';
import './LaptopsPage.css';

const LaptopsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const [laptops, setLaptops] = useState([]);
  const [filteredLaptops, setFilteredLaptops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');

  const [filters, setFilters] = useState({
    brand: '',
    priceRange: '',
    processor: '',
    ramSize: '',
    storageType: '',
    condition: '',
    screenSize: '',
    subcategory: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    fetchLaptops();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const subcategoryParam = urlParams.get('subcategory');
    const brandParam = urlParams.get('brand');

    if (subcategoryParam) {
      setSelectedCategory(subcategoryParam);
      setFilters((prev) => ({
        ...prev,
        subcategory: subcategoryParam
      }));
    }

    if (brandParam) {
      setSelectedBrand(brandParam);
      setFilters((prev) => ({
        ...prev,
        brand: brandParam
      }));
    }
  }, [location.search]);

  useEffect(() => {
    applyFilters();
  }, [laptops, filters, sortBy]);

  const fetchLaptops = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products', 'laptops', 'items'));
      const laptopData = querySnapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        const id = docSnapshot.id;
        const slug = generateProductSlug({ id, ...data });
        
        // If slug is missing or outdated, update it in background
        if (!data.slug || data.slug !== slug) {
          updateDoc(doc(db, 'products', 'laptops', 'items', id), { slug })
            .catch(err => console.warn('Slug update failed:', err));
        }
        
        return { id, ...data, slug };
      });
      setLaptops(laptopData);
    } catch (err) {
      setError('Failed to load laptops');
      console.error('Error fetching laptops:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...laptops];

    if (filters.subcategory) {
      filtered = filtered.filter(
        (laptop) => laptop.subcategory?.toLowerCase() === filters.subcategory.toLowerCase()
      );
    }
    if (filters.brand) {
      filtered = filtered.filter((laptop) => laptop.brand === filters.brand);
    }
    if (filters.processor) {
      filtered = filtered.filter((laptop) =>
        laptop.processor?.toLowerCase().includes(filters.processor.toLowerCase())
      );
    }
    if (filters.ramSize) {
      filtered = filtered.filter((laptop) => laptop.ramSize === filters.ramSize);
    }
    if (filters.storageType) {
      filtered = filtered.filter((laptop) => laptop.storageType === filters.storageType);
    }
    if (filters.condition) {
      filtered = filtered.filter((laptop) => laptop.condition === filters.condition);
    }
    if (filters.screenSize) {
      filtered = filtered.filter((laptop) => laptop.screenSize === filters.screenSize);
    }
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter((laptop) => {
        const price = Number(laptop.price) || 0;
        if (max) return price >= min && price <= max;
        return price >= min;
      });
    }

    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
        break;
      case 'price-high':
        filtered.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'discount':
        filtered.sort((a, b) => (Number(b.discount) || 0) - (Number(a.discount) || 0));
        break;
      default:
        break;
    }

    filtered.sort((a, b) => {
      const aAvailable = a.availability === 'Available';
      const bAvailable = b.availability === 'Available';

      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;
      return 0;
    });

    setFilteredLaptops(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      priceRange: '',
      processor: '',
      ramSize: '',
      storageType: '',
      condition: '',
      screenSize: '',
      subcategory: ''
    });
    setSelectedCategory('');
    setSelectedBrand('');
    navigate('/laptops', { replace: true });
  };

  const handleProductClick = (laptop) => {
    const slug = generateProductSlug(laptop);
    navigate(`/product/${slug || laptop.id}`);
  };

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrls: product.imageUrls,
      features: [
        product.processor,
        product.ramSize,
        `${product.storageSize || ''} ${product.storageType || ''}`.trim()
      ].filter(Boolean),
      quantity: 1
    };

    addToCart(cartItem);
    soundEffects.playAddToCartSound();
  };

  const getUniqueValues = (key) => {
    return [...new Set(laptops.map((laptop) => laptop[key]).filter(Boolean))];
  };

  return (
    <div className="laptops-page">
      <Helmet>
        <title>Affordable Refurbished Laptops | Best Prices & Certified Quality | Nexawave</title>
        <meta name="description" content="Shop the best deals on certified refurbished laptops. Affordable, high-performance laptops from top brands at unbeatable prices. Certified quality at the lowest price." />
        <meta name="keywords" content="affordable laptops, refurbished laptops, best price laptops, certified used laptops, Nexawave" />
      </Helmet>
      <Products2Hero 
        className="hero-tight"
        tagline="PREMIUM PERFORMANCE · CERTIFIED REFURBISHED · 12-MONTH WARRANTY"
        title1="Laptops"
        title2=""
        title3=""
        description=""
      />

      <div className="container" id="laptops-collection">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading laptops...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div className="filter-section">
              <div className="filter-header">
                <div className="results-info">
                  <h2>
                    Showing {filteredLaptops.length}{' '}
                    {selectedBrand ? selectedBrand : selectedCategory ? selectedCategory.toLowerCase() : ''} laptops
                  </h2>
                  <p>
                    {selectedBrand
                      ? `Perfect ${selectedBrand} devices for your needs`
                      : selectedCategory
                        ? `Perfect ${selectedCategory.toLowerCase()} devices for your needs`
                        : 'Find your perfect device'}
                  </p>
                </div>
                <div className="filter-controls">
                  <button
                    className={`filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                    </svg>
                    Filters
                  </button>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest First</option>
                    <option value="discount">Best Deals</option>
                  </select>
                </div>
              </div>

              <div className={`filter-panel ${showFilters ? 'show' : ''}`}>
                <div className="filter-grid">
                  <div className="filter-group">
                    <label>Subcategory</label>
                    <select
                      value={filters.subcategory}
                      onChange={(e) => handleFilterChange('subcategory', e.target.value)}
                    >
                      <option value="">All Subcategories</option>
                      {getUniqueValues('subcategory').map((subcategory) => (
                        <option key={subcategory} value={subcategory}>
                          {subcategory}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Brand</label>
                    <select
                      value={filters.brand}
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                    >
                      <option value="">All Brands</option>
                      {getUniqueValues('brand').map((brand) => (
                        <option key={brand} value={brand}>
                          {brand}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Price Range</label>
                    <select
                      value={filters.priceRange}
                      onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    >
                      <option value="">All Prices</option>
                      <option value="0-25000">Under ₹25,000</option>
                      <option value="25000-50000">₹25,000 - ₹50,000</option>
                      <option value="50000-75000">₹50,000 - ₹75,000</option>
                      <option value="75000-100000">₹75,000 - ₹1,00,000</option>
                      <option value="100000">Above ₹1,00,000</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Processor</label>
                    <select
                      value={filters.processor}
                      onChange={(e) => handleFilterChange('processor', e.target.value)}
                    >
                      <option value="">All Processors</option>
                      <option value="Intel Core i3">Intel Core i3</option>
                      <option value="Intel Core i5">Intel Core i5</option>
                      <option value="Intel Core i7">Intel Core i7</option>
                      <option value="AMD Ryzen">AMD Ryzen</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>RAM</label>
                    <select
                      value={filters.ramSize}
                      onChange={(e) => handleFilterChange('ramSize', e.target.value)}
                    >
                      <option value="">All RAM</option>
                      {getUniqueValues('ramSize').map((ram) => (
                        <option key={ram} value={ram}>
                          {ram}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Storage Type</label>
                    <select
                      value={filters.storageType}
                      onChange={(e) => handleFilterChange('storageType', e.target.value)}
                    >
                      <option value="">All Storage</option>
                      {getUniqueValues('storageType').map((storage) => (
                        <option key={storage} value={storage}>
                          {storage}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Condition</label>
                    <select
                      value={filters.condition}
                      onChange={(e) => handleFilterChange('condition', e.target.value)}
                    >
                      <option value="">All Conditions</option>
                      {getUniqueValues('condition').map((condition) => (
                        <option key={condition} value={condition}>
                          {condition}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Screen Size</label>
                    <select
                      value={filters.screenSize}
                      onChange={(e) => handleFilterChange('screenSize', e.target.value)}
                    >
                      <option value="">All Sizes</option>
                      {getUniqueValues('screenSize').map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-actions">
                    <button onClick={clearFilters} className="clear-filters">
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="laptops-grid">
              {filteredLaptops.map((laptop) => (
                <div key={laptop.id} className="laptop-card-wrapper">
                  <ProductCard
                    product={{
                      image: laptop.imageUrls?.[0] || '/api/placeholder/300/200',
                      imageAlt: laptop.name,
                      badge: laptop.availability === 'Available' ? 'In Stock' : 'Out of Stock',
                      isNew: false,
                      stockLeft: laptop.stockLeft,
                      brand: laptop.brand || 'NexaWave',
                      name: laptop.name,
                      subtitle: [laptop.condition, laptop.warranty].filter(Boolean).join(' | '),
                      scentNote: [
                        laptop.processor,
                        laptop.ramSize,
                        [laptop.storageSize, laptop.storageType].filter(Boolean).join(' ')
                      ]
                        .filter(Boolean)
                        .join(' | '),
                      rating: Number(laptop.rating) || 4.8,
                      reviews: Number(laptop.reviews) || 120,
                      originalPrice: Number(laptop.originalPrice) || Number(laptop.price) || 0,
                      salePrice: Number(laptop.price) || Number(laptop.originalPrice) || 0
                    }}
                    onCardClick={() => handleProductClick(laptop)}
                    onAddToCart={() => handleAddToCart(laptop)}
                    canAddToCart={laptop.availability === 'Available'}
                    showRating={false}
                    titleTwoLine={true}
                  />
                </div>
              ))}
            </div>

            {filteredLaptops.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No laptops found</h3>
                <p>Try adjusting your filters to see more results</p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LaptopsPage;
